import classNames from 'classnames';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {NucleotideType} from 'biojs/BioConstants';
import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import {NucleotideTypeToggle} from 'molbio/NucleotideTypeToggle';
import StudySearchInput, {StudySearchItem} from 'studies/StudySearchInput';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import useOnMount from 'ui-library/hooks/useOnMount';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';

export interface ImportFromFileProps {
  initialFolderId?: string;
  onMount: () => void;
  onUpdateNucleotideType: (nucleotideType: NucleotideType) => void;
  onUpdateSelectedFolderId: (folderId: string | undefined) => void;
  onUpdateStudySearchItems?: (StudySearchItems: Array<StudySearchItem> | undefined) => void;
}

export default function ImportFromFile(props: ImportFromFileProps): JSX.Element {
  /*
   * Instead of doing two-way data syncing between Backbone and React, we're only updating state from React
   * and syncing that state one way back to Backbone. This way, Backbone doesn't manipulate the parts of the
   * DOM that are updated in React, and vice versa. Because React state never interacts with any of the
   * Backbone-y parts of the DOM, the corresponding parts of the React virtual DOM never update, so React
   * never tries to change those parts of the actual DOM, so it doesn't squash any changes.
   * TODO BNCH-39516: Ideally we will just convert this whole thing to React.
   */
  useOnMount(() => {
    props.onMount();
  });
  const [folderId, setFolderId] = useState(props.initialFolderId);
  const [nucleotideType, setNucleotideType] = useState<NucleotideType>(NucleotideType.DNA);
  const [studySearchItems, setStudySearchItems] = useState<Array<StudySearchItem>>([]);
  return (
    <>
      <div className="u-small-spacing-left u-small-spacing-right">
        <p className="u-spacing-bottom">
          <b>Upload any DNA file</b> (Genbank, FASTA, ApE, Geneious, SnapGene, SeqBuilder v15 or below, etc.)
          to make a Benchling sequence. Drag in multiple at once!
        </p>
        <Row>
          <Col md={12}>
            <NucleotideTypeToggle
              nucleotideType={nucleotideType}
              onChangeSelection={(nucleotideType) => {
                setNucleotideType(nucleotideType);
                props.onUpdateNucleotideType(nucleotideType);
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col md={BenchlingConfig.STUDIES_ENABLE ? 6 : 12}>
            <div>
              <FieldLabel label={DEFAULT_PROJECT_FOLDER_LABEL_TEXT} />
            </div>
            <div>
              <FolderSelector
                buttonSize={ButtonSize.MEDIUM}
                value={folderId}
                onChange={({target: {value: newFolder}}) => {
                  setFolderId(newFolder);
                  props.onUpdateSelectedFolderId(newFolder);
                }}
                showInventory={true}
                enableRedesign={true}
                unsetOnFetchFail={true}
              />
            </div>
          </Col>
          {BenchlingConfig.STUDIES_ENABLE && (
            <Col md={6}>
              <div className="form-group">
                <FieldLabel label="Studies" />
                <StudySearchInput
                  onChange={(studySearchItems: Array<StudySearchItem>) => {
                    setStudySearchItems(studySearchItems);
                    props.onUpdateStudySearchItems!(studySearchItems);
                  }}
                  shouldAllowMultiple={true}
                  selectedItems={studySearchItems}
                />
              </div>
            </Col>
          )}
        </Row>
      </div>
      <form>
        <input
          type="file"
          multiple={true}
          id="sequence-upload"
          className="hidden"
          {...getTestProps({
            component: 'ImportFromFile',
            element: 'sequenceUpload',
          })}
        />
        <div className="drop-help">
          <span className="glyphicon glyphicon-cloud-upload" />
          <span className="instructions">Drag and drop files to upload or</span>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            className="file-choose u-mini-spacing-left"
            size={ButtonSize.SMALL}
            text="Choose a file"
          />
        </div>
      </form>
      <div className="alert alert-warning js-genBankFilenameWarning hidden">
        <strong>Note</strong>
        <span>
          : GenBank sequences use the <i>LOCUS</i> for the sequence name. To use the filename instead,{' '}
          <a className="js-renameGenBank">click here</a>.
        </span>
      </div>
      <div className="with-sequences hidden">
        <div className="import-context-bar-wrapper">
          <div className="import-context-bar">
            <div className="container">
              <div className="row">
                <div className="col col-sm-12">
                  <div className="import-context-bar-inner">
                    <div className="pull-right errors-only">
                      <label>
                        <input type="checkbox" />
                        Show errors only
                      </label>
                    </div>
                    <div className="pull-right progress import-progress hidden">
                      <div style={{width: '0%'}} className="progress-bar" />
                    </div>
                    <div className="u-vertical-center-items">
                      <input type="checkbox" className="select-all" />
                      <div className="buttonGroup u-tiny-spacing-left">
                        <a
                          data-toggle="dropdown"
                          className={classNames(
                            'button button--small button--tertiaryGray',
                            'dropdown-toggle is-disabled sequence-action open-button'
                          )}
                        >
                          Open
                        </a>
                      </div>
                      <div className="buttonGroup library-group u-tiny-spacing-left">
                        <button
                          type="button"
                          data-toggle="dropdown"
                          className={classNames(
                            'button button--small button--tertiaryGray',
                            'dropdown-toggle is-disabled move-to-button sequence-action'
                          )}
                        >
                          Move To
                        </button>
                      </div>
                      <div className="buttonGroup topology-group u-tiny-spacing-left">
                        <button
                          type="button"
                          data-toggle="dropdown"
                          className={classNames(
                            'button button--small button--tertiaryGray',
                            'dropdown-toggle is-disabled sequence-action'
                          )}
                        >
                          Set Topology
                          <span className="caret" />
                        </button>
                        <ul className="dropdown-menu topology-options">
                          <li className="topology-option-linear">
                            <a href="#" data-circular="0">
                              Linear
                            </a>
                          </li>
                          <li className="topology-option-circular">
                            <a href="#" data-circular="1">
                              Circular
                            </a>
                          </li>
                        </ul>
                      </div>
                      <div className="buttonGroup tagdropdown u-tiny-spacing-left">
                        <button
                          type="button"
                          data-toggle="dropdown"
                          className={classNames(
                            'button button--small button--tertiaryGray',
                            'dropdown-toggle is-disabled sequence-action tag-button'
                          )}
                        >
                          Edit Tags
                          <span className="caret" />
                        </button>
                        <div className="dropdown-menu add-tag-menu">
                          <input type="text" className="tag-search" />
                          <ul className="tag-options" />
                          <button type="button" className="button button--small button--primary create-tag">
                            Create New Tag
                          </button>
                          <button
                            type="button"
                            className="button button--small button--tertiaryGray apply-tags"
                          >
                            Apply Tags
                          </button>
                        </div>
                      </div>
                      <div className="buttonGroup auto-annotate-group u-tiny-spacing-left">
                        <button
                          type="button"
                          data-toggle="dropdown"
                          className={classNames(
                            'button button--small button--tertiaryGray',
                            'dropdown-toggle is-disabled auto-annotate-button sequence-action'
                          )}
                        >
                          Auto-annotate
                        </button>
                      </div>
                      {BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS && (
                        <button
                          type="button"
                          className={classNames(
                            'button button--small button--tertiaryGray is-disabled',
                            'u-tiny-spacing-left sequence-action add-to-worklist-button'
                          )}
                          data-toggle="tooltip"
                          title="Add to worklist"
                        >
                          <BenchlingIcon icon="worklist-add" />
                        </button>
                      )}
                      <button
                        type="button"
                        className={classNames(
                          'button button--small button--negative is-disabled',
                          'u-tiny-spacing-left sequence-action delete-button'
                        )}
                      >
                        <span className="glyphicon glyphicon-trash" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ul className="uploading-files" />
    </>
  );
}
